<template>
  <div class="ngo-landing-page">
    <section class="ngo-hero">
      <div class="ngo-hero__backdrop"></div>
      <div class="ngo-hero__content container">
        <div class="ngo-hero__copy">
          <p class="hero-kicker">NGO Inventory</p>
          <h1>Non-Governmental Organisations</h1>
          <p class="hero-description">
            A central workspace for NGO registration, registered NGO visibility, annual reporting,
            and practical resources for health-focused organizations in Nigeria.
          </p>
        </div>

        <div class="hero-summary">
          <div class="hero-stat">
            <span class="hero-stat__label">Access</span>
            <strong class="hero-stat__value">Registration and reporting guidance</strong>
          </div>
          <div class="hero-stat">
            <span class="hero-stat__label">Designed for</span>
            <strong class="hero-stat__value">NGO coordination and visibility</strong>
          </div>
        </div>
      </div>
    </section>

    <section id="card-section" class="ngo-actions container">
      <div class="section-heading">
        <div>
          <p class="section-kicker">Action Center</p>
          <h2>Choose the NGO workspace you need</h2>
        </div>
      </div>

      <div class="row justify-content-center">
        <div v-if="canUploadNgo" class="col-12 col-md-6 col-xl-3 mb-4" id="uploadngo">
          <router-link to="/ngo/upload" class="action-link">
            <article class="action-card action-card--admin">
              <div class="action-card__header">
                <div class="action-card__icon action-card__icon--admin">
                  <img src="../../../../assets/ngoimgs/surface1.png" class="img-fluid" width="42" />
                </div>
                <h3>Upload NGO</h3>
              </div>
              <div class="action-card__copy">
                <p>Add a new NGO profile directly as an admin using the upload workspace.</p>
              </div>
            </article>
          </router-link>
        </div>

        <div v-if="isLoggedIn" class="col-12 col-md-6 col-xl-3 mb-4" id="registerngo">
          <router-link
            :to="isLoggedIn === true ? 'registration-form' : '/auth/login'"
            class="action-link"
          >
            <article class="action-card">
              <div class="action-card__header">
                <div class="action-card__icon">
                  <img src="../../../../assets/ngoimgs/surface1.png" class="img-fluid" width="42" />
                </div>
                <h3>Register your NGO</h3>
              </div>
              <div class="action-card__copy">
                <p>Start the registration process and provide your organization profile.</p>
              </div>
            </article>
          </router-link>
        </div>

        <div class="col-12 col-md-6 col-xl-3 mb-4" id="viewallngo">
          <router-link to="registered" class="action-link">
            <article class="action-card">
              <div class="action-card__header">
                <div class="action-card__icon">
                  <img src="../../../../assets/ngoimgs/surface1.png" class="img-fluid" width="42" />
                </div>
                <h3>View all registered NGOs</h3>
              </div>
              <div class="action-card__copy">
                <p>Browse NGOs in Nigeria that have been duly registered with the Ministry.</p>
              </div>
            </article>
          </router-link>
        </div>

        <div class="col-12 col-md-6 col-xl-3 mb-4" id="forum">
          <router-link to="resources" class="action-link">
            <article class="action-card">
              <div class="action-card__header">
                <div class="action-card__icon">
                  <img
                    src="../../../../assets/ngoimgs/Group 1123.png"
                    class="img-fluid"
                    width="50"
                  />
                </div>
                <h3>NGO resources</h3>
              </div>
              <div class="action-card__copy">
                <p>Access useful resources, supporting links, and practical NGO guidance.</p>
              </div>
            </article>
          </router-link>
        </div>
      </div>
    </section>

    <section class="container info-section" id="info-section">
      <div class="info-shell">
        <div class="info-shell__header">
          <div>
            <p class="section-kicker">Overview</p>
            <h2>Why this NGO inventory exists</h2>
          </div>
        </div>

        <v-tour name="toursguide" :steps="steps">
          <template slot-scope="tour">
            <transition name="fade">
              <v-step
                v-if="tour.steps[tour.currentStep]"
                :key="tour.currentStep"
                :step="tour.steps[tour.currentStep]"
                :previous-step="tour.previousStep"
                :next-step="tour.nextStep"
                :stop="tour.stop"
                :skip="tour.skip"
                class="v-tour-step"
                :is-first="tour.isFirst"
                :is-last="tour.isLast"
                :labels="tour.labels"
              >
                <template>
                  <div slot="actions" class="tour-actions">
                    <button
                      v-if="tour.currentStep > 0"
                      @click="tour.previousStep"
                      class="tour-btn"
                      type="button"
                    >
                      Previous
                    </button>
                    <button @click="tour.nextStep" class="tour-btn" type="button">Next</button>
                    <button
                      v-if="tour.currentStep === 2"
                      @click="tour.skip"
                      class="tour-btn"
                      type="button"
                    >
                      Finish
                    </button>
                  </div>
                </template>
              </v-step>
            </transition>
          </template>
        </v-tour>

        <div class="info-grid">
          <div class="info-copy">
            <p class="info-paragraph">
              Non-Governmental Organisations (NGOs) branch of the Health System Strengthening
              division of the Department of Health Planning, Research and Statistics was created by
              the Federal Ministry of Health to coordinate the activities of health sector NGOs
              nationwide.
            </p>
            <p class="info-paragraph">
              This resource serves as a database of all registered health NGOs across the country
              and provides the Ministry and stakeholders with necessary information on NGOs and
              their focus areas, to aid proper coordination of health interventions in Nigeria.
            </p>

            <div v-if="extended" class="objective-list">
              <p class="objective-list__title">The objectives are:</p>
              <ul>
                <li>
                  To recognize all NGOs actively carrying out interventions in the health space.
                </li>
                <li>
                  To provide the Ministry and stakeholders with information about all NGOs working
                  in the health sector for better coordination of health interventions in the
                  country.
                </li>
                <li>
                  To disseminate information on priorities of the health sector, providing guidance
                  to NGOs on health programs and interventions.
                </li>
              </ul>
            </div>

            <button type="button" class="toggle-link" @click="extended = !extended">
              <i class="fa fa-angle-down mx-1"></i>
              <span>Show {{ extended ? 'less' : 'more' }}</span>
            </button>
          </div>

          <div class="info-highlights">
            <div class="highlight-card">
              <span class="highlight-card__label">Primary role</span>
              <strong class="highlight-card__value">National NGO coordination</strong>
            </div>
            <div class="highlight-card">
              <span class="highlight-card__label">Value</span>
              <strong class="highlight-card__value">Better visibility into NGO focus areas</strong>
            </div>
            <div class="highlight-card">
              <span class="highlight-card__label">Supports</span>
              <strong class="highlight-card__value"
                >Planning, reporting, and health sector alignment</strong
              >
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'main-landing',
  data() {
    return {
      tourEnabled: true,
      tourShown: 1,
      tour: null,
      varia: 5,
      steps: [
        {
          target: '#registerngo',
          content: '',
          id: '#registerngo',
        },
        {
          target: '#viewallngo',
          content: '',
        },
        {
          target: '#forum',
          content: '',
        },
      ],
      extended: false,
    };
  },
  mounted() {
    this.tourShown = localStorage.getItem('tourShown');
    const value = this.tourShown == null;

    if (value) {
      localStorage.setItem('tourShown', 'Tour has been shown');
      this.steps[0].content = this.tourContentForStep1;
      this.steps[1].content = this.tourContentForStep2;
      this.steps[2].content = this.tourContentForStep3;
      this.$tours.toursguide.start();
    }
  },
  computed: {
    ...mapGetters('auth', ['user', 'isLoggedIn', 'isAdmin']),
    canUploadNgo() {
      return [1, 2].includes(Number(this.user && this.user.type));
    },
    tourContentForStep1() {
      return `<div>1/${this.steps.length}</div> <div>To Register Your NGO Click this Dialogue</div>`;
    },
    tourContentForStep2() {
      return `<div>2/${this.steps.length}</div> <div>To View All Registered NGOs Please Click this Dialogue</div>`;
    },
    tourContentForStep3() {
      return `<div>3/${this.steps.length}</div> <div>To Visit the Forum Platform Click this Dialogue</div>`;
    },
  },
};
</script>

<style scoped>
.ngo-landing-page {
  padding-bottom: 2rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.12), transparent 24%),
    linear-gradient(180deg, #f4faf5 0%, #eef5ef 100%);
}

.ngo-hero {
  position: relative;
  overflow: hidden;
  min-height: 28rem;
  background-image: url('../../../../assets/ngoimgs/annie-spratt-cVEOh_JJmEE-unsplash.png');
  background-position: center;
  background-size: cover;
  border-radius: 0 0 34px 34px;
  box-shadow: 0 24px 80px rgba(17, 41, 24, 0.14);
}

.ngo-hero__backdrop {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(8, 28, 15, 0.88), rgba(16, 56, 28, 0.68)),
    linear-gradient(180deg, rgba(9, 32, 17, 0.35), rgba(9, 32, 17, 0.82));
}

.ngo-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  min-height: 28rem;
  padding: 4rem 1rem 3.2rem;
  color: #ffffff;
}

.ngo-hero__copy {
  max-width: 46rem;
}

.hero-kicker,
.section-kicker,
.hero-stat__label,
.highlight-card__label {
  display: block;
  margin-bottom: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-kicker {
  color: rgba(255, 255, 255, 0.76);
}

.ngo-hero__copy h1 {
  margin-bottom: 0.8rem;
  font-size: clamp(2.2rem, 4vw, 4rem);
  line-height: 1.06;
  color: #ffffff;
}

.hero-description {
  margin: 0;
  max-width: 38rem;
  font-size: 1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.84);
}

.hero-summary {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  min-width: 18rem;
}

.hero-stat {
  padding: 1rem 1.1rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(14px);
}

.hero-stat__label {
  color: rgba(255, 255, 255, 0.72);
}

.hero-stat__value {
  color: #ffffff;
}

.ngo-actions,
.info-section {
  margin-top: 3.5rem;
}

.section-heading {
  margin-bottom: 1.4rem;
}

.section-kicker {
  color: #2c8d41;
}

.section-heading h2,
.info-shell__header h2 {
  margin: 0;
  color: #132218;
}

.action-link {
  display: block;
  height: 100%;
  text-decoration: none;
}

.action-card {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 0.85rem;
  height: 100%;
  min-height: 11.75rem;
  padding: 1.15rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(244, 250, 245, 0.96)), #ffffff;
  box-shadow: 0 20px 45px rgba(20, 55, 31, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease,
    background 0.25s ease;
  position: relative;
  overflow: hidden;
}

.action-card:hover {
  transform: translateY(-4px);
  border-color: rgba(48, 171, 76, 0.16);
  box-shadow: 0 28px 55px rgba(20, 55, 31, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.82);
}

.action-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(48, 171, 76, 0.08), transparent 45%);
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}

.action-card:hover::after {
  opacity: 1;
}

.action-card__header {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
}

.action-card--admin {
  background: linear-gradient(180deg, rgba(233, 247, 237, 0.98), rgba(245, 250, 246, 0.96)), #ffffff;
  border-color: rgba(48, 171, 76, 0.18);
}

.action-card__icon--admin {
  background: linear-gradient(135deg, rgba(48, 171, 76, 0.16), rgba(20, 98, 43, 0.12));
}

.action-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 18px;
  background: rgba(48, 171, 76, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.action-card__header h3 {
  margin: 0;
  color: #183322;
  font-size: 1.05rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.action-card__copy p {
  margin: 0;
  color: #567063;
  font-size: 0.94rem;
  line-height: 1.55;
}

.action-card__copy {
  min-width: 0;
  width: 100%;
}

.info-shell {
  padding: 1.8rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(245, 250, 246, 0.96)), #ffffff;
  box-shadow: 0 24px 60px rgba(20, 55, 31, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.info-shell__header {
  margin-bottom: 1.3rem;
}

.info-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 1.4rem;
}

.info-paragraph,
.objective-list__title,
.objective-list li {
  color: #4f685a;
  line-height: 1.8;
}

.objective-list {
  margin-top: 1rem;
}

.objective-list ul {
  padding-left: 1.25rem;
  margin-bottom: 0;
}

.objective-list li + li {
  margin-top: 0.55rem;
}

.toggle-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 1rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #2c8d41;
  font-weight: 700;
}

.info-highlights {
  display: grid;
  gap: 1rem;
}

.highlight-card {
  padding: 1.2rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
}

.highlight-card__label {
  color: #2c8d41;
}

.highlight-card__value {
  color: #183322;
  line-height: 1.5;
}

.v-tour-step {
  background-color: #2f9f49 !important;
  border-radius: 18px !important;
  box-shadow: 0 18px 36px rgba(31, 111, 51, 0.2) !important;
}

.tour-actions {
  display: flex;
  gap: 0.5rem;
}

.tour-btn {
  border-radius: 10px;
  border: 0;
  padding: 0.55rem 0.85rem;
  background: rgba(255, 255, 255, 0.16);
  color: #ebebeb;
}

@media screen and (max-width: 991px) {
  .ngo-hero__content,
  .info-grid {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .hero-summary {
    min-width: 0;
    width: 100%;
  }

  .action-card {
    min-height: 10.5rem;
  }
}

@media screen and (max-width: 767px) {
  .ngo-hero {
    border-radius: 0 0 24px 24px;
  }

  .ngo-hero__content {
    min-height: auto;
    padding: 3rem 1rem 2.5rem;
  }

  .action-card {
    min-height: auto;
  }

  .info-shell {
    padding: 1.25rem;
    border-radius: 22px;
  }
}
</style>
